import { Box, Checkbox, Divider, Flex, Text, VStack } from "@chakra-ui/react";
import React from "react";

import AiInterviewerSectionTitle from "./AiInterviewerSectionTitle";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

type NotificationRowProps = {
  title: string;
  description: string;
  emailEnabled: boolean;
  emailDisabled?: boolean;
  onEmailChange?: (checked: boolean) => void;
  readOnly: boolean;
  showDivider: boolean;
  smsSection?: React.ReactNode;
};

const NotificationRow: React.FC<NotificationRowProps> = ({
  title,
  description,
  emailEnabled,
  emailDisabled = false,
  onEmailChange,
  readOnly,
  showDivider,
  smsSection,
}) => {
  return (
    <>
      <Flex gap={14} py={5} align="flex-start" w="100%">
        <VStack flex="1" align="stretch" spacing={2}>
          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
            {title}
          </Text>
          <Text fontSize="xs" color="gray.800" lineHeight="18px">
            {description}
          </Text>
          {smsSection}
        </VStack>
        <Box w="43px" pt="2px">
          <Checkbox
            size="lg"
            isChecked={emailEnabled}
            isDisabled={emailDisabled || readOnly}
            onChange={(e) => onEmailChange?.(e.target.checked)}
          />
        </Box>
      </Flex>
      {showDivider && <Divider borderColor="gray.200" />}
    </>
  );
};

type AiInterviewerNotificationsSettingsProps = {
  readOnly: boolean;
  aiInterviewSmsEnabled: boolean;
};

const AiInterviewerNotificationsSettings: React.FC<
  AiInterviewerNotificationsSettingsProps
> = ({ readOnly, aiInterviewSmsEnabled }) => {
  const { watch, setValue } = useAiInterviewerFormContext();

  const reminderEmailEnabled = watch("reminderEmailEnabled");
  const completionEmailEnabled = watch("completionEmailEnabled");
  const invitationSmsEnabled = watch("invitationSmsEnabled");

  return (
    <VStack spacing={0} align="stretch" w="100%">
      <Box pb={5}>
        <AiInterviewerSectionTitle mb={1} title="Notification Settings" />
        <Text color="gray.600" fontSize="sm">
          Customize which notifications are sent to candidates.
        </Text>
      </Box>

      <Flex gap={14} h="40px" align="center" pt={5}>
        <Text flex="1" fontSize="sm" fontWeight="semibold" color="gray.500">
          Notification
        </Text>
        <Box w="51px">
          <Text fontSize="sm" fontWeight="semibold" color="gray.500">
            Email
          </Text>
        </Box>
      </Flex>
      <Divider borderColor="gray.200" />

      <NotificationRow
        title="Invitation"
        description="Sent when a candidate is invited to complete an AI interview. Includes a secure link to get started."
        emailEnabled
        emailDisabled
        readOnly={readOnly}
        showDivider
        smsSection={
          aiInterviewSmsEnabled ? (
            <Checkbox
              size="sm"
              isChecked={invitationSmsEnabled ?? false}
              isDisabled={readOnly}
              onChange={(e) =>
                setValue("invitationSmsEnabled", e.target.checked, {
                  shouldDirty: true,
                })
              }
            >
              <Text fontSize="xs" color={readOnly ? "gray.400" : "gray.800"}>
                Also send via SMS
              </Text>
            </Checkbox>
          ) : undefined
        }
      />
      <NotificationRow
        title="Reminder"
        description="Sent to candidates who haven't started their interview after being invited."
        emailEnabled={reminderEmailEnabled ?? false}
        onEmailChange={(checked) =>
          setValue("reminderEmailEnabled", checked, { shouldDirty: true })
        }
        readOnly={readOnly}
        showDivider
      />
      <NotificationRow
        title="Completion"
        description="Sent after a candidate completes their interview to confirm submission."
        emailEnabled={completionEmailEnabled ?? false}
        onEmailChange={(checked) =>
          setValue("completionEmailEnabled", checked, { shouldDirty: true })
        }
        readOnly={readOnly}
        showDivider={false}
      />
    </VStack>
  );
};

export default AiInterviewerNotificationsSettings;
